import { lazy } from "react"
import ScoutingMainLayout from "layout/scouting/MainLayout"
import ScoutingCommonLayout from "layout/scouting/CommonLayout"
import Loadable from "components/Loadable"
import AuthGuard from "utils/route-guard/AuthGuard"
import ReportUploadRequestsPage from "pages/scouting/report-upload-requests/ReportUploadRequestsPage"

const MaintenanceError = Loadable(lazy(() => import("pages/maintenance/error/404")))
const MaintenanceError500 = Loadable(lazy(() => import("pages/maintenance/error/500")))
const MaintenanceUnderConstruction = Loadable(
  lazy(() => import("pages/maintenance/under-construction/under-construction"))
)
const MaintenanceComingSoon = Loadable(lazy(() => import("pages/maintenance/coming-soon/coming-soon")))

const PlaylistPage = Loadable(lazy(() => import("pages/scouting/playlist/PlaylistPage")))
const PlaylistDetail = Loadable(lazy(() => import("pages/scouting/playlist/PlaylistDetail")))
const TracksPage = Loadable(lazy(() => import("pages/scouting/tracks/TracksPage")))
const ArtistsPage = Loadable(lazy(() => import("pages/scouting/artists/ArtistsPage")))
const SpotifyArtistRequestPage = Loadable(
  lazy(() => import("pages/scouting/spotify-artist-request/SpotifyArtistRequestPage"))
)
const ArtistsDetail = Loadable(lazy(() => import("pages/scouting/artists/ArtistDetail")))
const AlbumsPage = Loadable(lazy(() => import("pages/scouting/albums/AlbumsPage")))
const AlbumDetail = Loadable(lazy(() => import("pages/scouting/albums/AlbumDetail")))
const ArtistAnalysisPage = Loadable(lazy(() => import("pages/scouting/artists/analysis/ArtistAnalysisPage")))
const BlacklistPage = Loadable(lazy(() => import("pages/scouting/blacklist/BlacklistPage")))
const OffersReports = Loadable(lazy(() => import("pages/scouting/offers-reports/OffersReports")))
const ConfirmedOffersPage = Loadable(lazy(() => import("pages/scouting/confirmed-offers/ConfirmedOffersPage")))
const ConfirmedOfferDetail = Loadable(lazy(() => import("pages/scouting/confirmed-offers/detail/ConfirmedOfferDetail")))
const DealOffersReportPage = Loadable(lazy(() => import("pages/scouting/confirmed-offers/deal-offers/DealOffersReportPage")))
const CompanyBreakdownPage = Loadable(lazy(() => import("pages/scouting/company-breakdown/CompanyBreakdownPage")))
const CompanyPage = Loadable(lazy(() => import("pages/scouting/company/CompanyPage")))
const AffiliatePage = Loadable(lazy(() => import("pages/scouting/affiliate/AffiliatePage")))
const SystemSettings = Loadable(lazy(() => import("pages/scouting/admin/system/SystemSettings")))
const TaskStatuses = Loadable(lazy(() => import("pages/scouting/admin/task-statuses/TaskStatuses")))
const PipelineActions = Loadable(lazy(() => import("pages/scouting/admin/pipeline-actions/PipelineActions")))
const SchedulerLog = Loadable(lazy(() => import("pages/scouting/admin/scheduler/SchedulerLog")))
const AllowedOfferLinks = Loadable(lazy(() => import("pages/scouting/admin/allowed-offer-links/AllowedOfferLinks")))
const UsersPage = Loadable(lazy(() => import("pages/scouting/admin/user-list/UsersPage")))
const AnalyticsUsersLogs = Loadable(lazy(() => import("pages/scouting/admin/analytics-user-logs/AnalyticsUserLogs")))
const AnalyticsScoutLogs = Loadable(lazy(() => import("pages/scouting/admin/analytics-scout-logs/AnalyticsScoutLogs")))
const TagsPage = Loadable(lazy(() => import("pages/scouting/admin/tags/TagsPage")))
const UnitPricePage = Loadable(lazy(() => import("pages/scouting/admin/unit-price/UnitPricePage")))
const ArtistPipelineDetail = Loadable(lazy(() => import("pages/scouting/artists/pipeline/ArtistPipelineDetail")))
const CompanyBreakdownDetail = Loadable(lazy(() => import("pages/scouting/company-breakdown/CompanyBreakdownDetail")))
const TasksPage = Loadable(lazy(() => import("pages/scouting/task/Tasks")))
const InboxPage = Loadable(lazy(() => import("pages/scouting/inbox/Inbox")))
const UnitPriceDashboard = Loadable(lazy(() => import("pages/scouting/unit-price-dashboard/UnitPriceDashboard")))
const ReportUploadColumnPage = Loadable(
  lazy(() => import("pages/scouting/admin/report-upload-column/ReportUploadColumnPage"))
)
const MessageContent = Loadable(lazy(() => import("pages/scouting/admin/message-content/MessageContent")))
const DistributorsPage = Loadable(lazy(() => import("pages/scouting/admin/distributors/DistributorsPage")))


const ScoutingRoutes = {
  path: "/",
  children: [
    {
      path: "/",
      element: (
        <AuthGuard>
          <ScoutingMainLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: "home",
          element: <div>WELCOME TO SCOUTING</div>
        },
        {
          path: "playlist",
          element: <PlaylistPage />
        },
        {
          path: "tracks",
          element: <TracksPage showFilter={true} showLabelColumns={true} showOwnerships={false} defaultLimit={10} />
        },
        {
          path: "artists",
          element: <ArtistsPage />
        },
        {
          path: "playlist/:playlistId",
          element: <PlaylistDetail />
        },
        {
          path: "artists/:artistIdParam",
          element: <ArtistsDetail showAlbums={true} showConfirmedOffers={true} />
        },
        {
          path: "artists/:artistId/analysis/:analysisId",
          element: <ArtistAnalysisPage />
        },
        {
          path: "pipeline/:artistIdParam",
          element: <ArtistPipelineDetail />
        },
        {
          path: "releases",
          element: <AlbumsPage artistIdProp={0} defaultLimit={10} />
        },
        {
          path: "releases/:albumId",
          element: <AlbumDetail />
        },
        {
          path: "blacklist",
          element: <BlacklistPage />
        },
        {
          path: "tasks",
          element: <TasksPage />
        },
        {
          path: "inbox",
          element: <InboxPage />
        },
        {
          path: "offers-reports",
          element: <OffersReports />
        },
        {
          path: "unit-price-dashboard",
          element: <UnitPriceDashboard />
        },
        {
          path: "spotify-artist-request",
          element: <SpotifyArtistRequestPage />
        },
        {
          path: "confirmed-offers",
          element: <ConfirmedOffersPage />
        },
        {
          path: "deals",
          element: <ConfirmedOffersPage isDealOffers={true} />
        },
        {
          path: "deals-report",
          element: <DealOffersReportPage />
        },
        {
          path: "confirmed-offer/:confirmedOfferId",
          element: <ConfirmedOfferDetail isPublic={false} />
        },
        {
          path: "scout",
          element: <AffiliatePage />
        },
        {
          path: "company",
          element: <CompanyPage />
        },
        {
          path: "company-breakdown",
          element: <CompanyBreakdownPage blackListPage={false} />
        },
        {
          path: "company-breakdown-detail/:pLabel",
          element: <CompanyBreakdownDetail />
        },
        {
          path: "report-upload-requests",
          element: <ReportUploadRequestsPage/>
        },
        {
          path: "admin/system-settings",
          element: <SystemSettings />
        },
        {
          path: "admin/task-statuses",
          element: <TaskStatuses />
        },
        {
          path: "admin/pipeline-actions",
          element: <PipelineActions />
        },
        {
          path: "admin/scheduler-logs",
          element: <SchedulerLog />
        },
        {
          path: "admin/allowed-offer-links",
          element: <AllowedOfferLinks />
        },
        {
          path: "admin/tags",
          element: <TagsPage />
        },
        {
          path: "admin/report-upload-columns",
          element: <ReportUploadColumnPage />
        },
        {
          path: "admin/unit-price",
          element: <UnitPricePage />
        },
        {
          path: "admin/users",
          element: <UsersPage />
        },
        {
          path: "admin/analytics-user-logs",
          element: <AnalyticsUsersLogs />
        },
        {
          path: "admin/analytics-scout-logs",
          element: <AnalyticsScoutLogs />
        },
        {
          path: "admin/message-content",
          element: <MessageContent />
        },
        {
          path: "admin/distributors",
          element: <DistributorsPage />
        }
      ]
    },
    {
      path: "/maintenance",
      element: <ScoutingCommonLayout />,
      children: [
        {
          path: "404",
          element: <MaintenanceError />
        },
        {
          path: "500",
          element: <MaintenanceError500 />
        },
        {
          path: "under-construction",
          element: <MaintenanceUnderConstruction />
        },
        {
          path: "coming-soon",
          element: <MaintenanceComingSoon />
        }
      ]
    }
  ]
}

export default ScoutingRoutes
